// src/components/PrestartModal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../services/firebase";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  limit,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

const TRI = [
  { v: "ok", label: "No Defect" },
  { v: "fault", label: "Fault Identified" },
  { v: "na", label: "N/A" },
];

// Checklist items
const CHECK_1 = [
  ["engineOil", "Engine Oil / Hydraulic / Auxiliary oil level OK"],
  ["tyres", "Tyres: visual integrity, wear & pressure"],
  ["wheelSecurity", "Wheel security (rims & nut indicators) OK"],
  ["coolant", "Coolant / water levels OK"],
  ["seatbelt", "Seatbelt & seat operational & condition OK"],
  ["airLines", "Air lines checked"],
  ["lights", "All lights/reflectors/lenses & indicators operational"],
  ["beacons", "Beacons & reverse lights (or buzzer) working"],
  ["mirrorsWindows", "Mirrors/windows/windscreen condition OK"],
  ["leaks", "No visible leaks (coolant, oil, fuel, air, other)"],
];

const CHECK_2 = [
  ["lifterCompactor", "Lifter/Compactor/Tank/Pump unit OK (if fitted)"],
  ["damagePanels", "Damage: panels/structural members secure"],
  ["kits", "Spill kits, first aid kits & safety triangles present/in date"],
  ["extinguisher", "Fire extinguisher secure and in date"],
  ["eStops", "E-Stops checked (all)"],
  ["scales", "Load scales/weighers checked & zero"],
  ["numberPlate", "Number plate secure/visible & light operational"],
  ["placards", "Signage/placards (road transport & WHS) displayed"],
  ["wipers", "Windscreen wipers/washers operational"],
  ["horn", "Horn operational"],
];

const CHECK_3 = [
  ["radio", "Communication radio operational"],
  ["cameras", "Camera(s) operational"],
  ["brakeGauges", "Brake gauges operational"],
  ["smartBrake", "Smart Brake operational"],
  ["parkBrake", "Park Brake operational"],
  ["footBrake", "Foot Brake rolling test carried out"],
];

function TriSelect({ name, value, onChange }) {
  return (
    <div className="flex gap-2">
      {TRI.map((opt) => (
        <label key={opt.v} className="inline-flex items-center gap-1 text-xs">
          <input
            type="radio"
            name={name}
            checked={value === opt.v}
            onChange={() => onChange(opt.v)}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

// Simple signature pad (no extra libs)
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches && e.touches[0];
      const x = (t ? t.clientX : e.clientX) - rect.left;
      const y = (t ? t.clientY : e.clientY) - rect.top;
      return { x, y };
    };

    const down = (e) => {
      drawing.current = true;
      ctx.beginPath();
      const p = getPos(e);
      ctx.moveTo(p.x, p.y);
      e.preventDefault();
    };
    const move = (e) => {
      if (!drawing.current) return;
      const p = getPos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      e.preventDefault();
    };
    const up = () => {
      drawing.current = false;
      onChange?.(canvas.toDataURL("image/png"));
    };

    canvas.addEventListener("mousedown", down);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);

    return () => {
      canvas.removeEventListener("mousedown", down);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={140}
        className="border rounded bg-white w-full"
      />
      <button
        type="button"
        onClick={clear}
        className="mt-2 text-xs px-2 py-1 rounded border"
      >
        Clear Signature
      </button>
    </div>
  );
}

export default function PrestartModal({
  driverId,
  date,
  onClose,
  onCompleted,
  driverEmail = "",
  prestartDocId,
  companyId: initialCompanyId,
  companyName: initialCompanyName,
  runKey = "",
  vehicleId: assignedVehicleId = "",
  vehicleRego: assignedVehicleRego = "",
  vehicleLoadType: assignedVehicleLoadType = "",
  vehicleLabel: assignedVehicleLabel = "",
}) {
  const [companyId, setCompanyId] = useState(initialCompanyId || null);
  const [companyName, setCompanyName] = useState(initialCompanyName || "");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [trucks, setTrucks] = useState([]);

  const hasAssignedVehicle = !!(
    assignedVehicleId ||
    assignedVehicleRego ||
    assignedVehicleLoadType ||
    assignedVehicleLabel
  );

  const [unitType, setUnitType] = useState(assignedVehicleLoadType || "");
  const [registrationNo, setRegistrationNo] = useState(assignedVehicleRego || "");
  const [selectedTruckId, setSelectedTruckId] = useState(assignedVehicleId || null);
  const [email, setEmail] = useState(driverEmail || "");
  const [odoStart, setOdoStart] = useState("");
  const [hourMeterStart, setHourMeterStart] = useState("");
  const [shiftStart, setShiftStart] = useState("");
  const [sig, setSig] = useState(null);

  const [decFit, setDecFit] = useState(true);
  const [decLicense, setDecLicense] = useState(true);
  const [decInspect, setDecInspect] = useState(true);

  const initTri = useMemo(() => {
    const obj = {};
    [...CHECK_1, ...CHECK_2, ...CHECK_3].forEach(([k]) => (obj[k] = "ok"));
    return obj;
  }, []);
  const [tri, setTri] = useState(initTri);
  const setTriVal = (k, v) => setTri((s) => ({ ...s, [k]: v }));

  const [comments, setComments] = useState({});
  const setComment = (k, v) => setComments((s) => ({ ...s, [k]: v }));

  const anyFault = useMemo(() => Object.values(tri).some((v) => v === "fault"), [tri]);

  const unitTypeOptions = useMemo(() => {
    const set = new Set();
    trucks.forEach((t) => {
      if (t.loadType) set.add(String(t.loadType));
    });
    return Array.from(set);
  }, [trucks]);

  const filteredTrucks = useMemo(() => {
    if (!unitType) return trucks;
    return trucks.filter((t) => String(t.loadType || "") === String(unitType));
  }, [trucks, unitType]);

  useEffect(() => {
    if (initialCompanyId) setCompanyId(initialCompanyId);
    if (initialCompanyName) setCompanyName(initialCompanyName);
  }, [initialCompanyId, initialCompanyName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!driverId || !date) return;
        const id = prestartDocId || `${driverId}_${date}`;
        const snap = await getDoc(doc(db, "prestarts", id));
        if (cancelled || !snap.exists()) return;
        const d = snap.data() || {};
        if (d.companyId && !companyId) setCompanyId(d.companyId);
        if (d.companyName && !companyName) setCompanyName(d.companyName);
        setUnitType(d.vehicleLoadType || d.unitType || assignedVehicleLoadType || "");
        setRegistrationNo(d.vehicleRego || d.registrationNo || assignedVehicleRego || "");
        setSelectedTruckId(d.vehicleId || d.truckId || assignedVehicleId || null);
        setEmail(d.email || driverEmail || "");
        setOdoStart(d.odometerStart != null && d.odometerStart !== "" ? String(d.odometerStart) : "");
        setHourMeterStart(d.hourMeterStart != null && d.hourMeterStart !== "" ? String(d.hourMeterStart) : "");
        setShiftStart(d.shiftStartTime || "");
        setSig(d.signature || null);
        const dec = d.declaration || {};
        setDecFit(dec.fitForDuty !== false);
        setDecLicense(dec.licenseTrainingPPE !== false);
        setDecInspect(dec.inspectionDone !== false);
        if (d.checklist) setTri((s) => ({ ...s, ...d.checklist }));
        if (d.comments) setComments(d.comments);
      } catch (e) {
        console.error("Failed to load existing prestart:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [driverId, date, prestartDocId, assignedVehicleId, assignedVehicleRego, assignedVehicleLoadType, driverEmail, companyId, companyName]);

  useEffect(() => {
    if (companyId || !driverId || !date) return;
    let cancelled = false;
    (async () => {
      try {
        const legacyId = `${driverId}_${date}`;
        const snap = await getDoc(doc(db, "assignments", legacyId));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() || {};
          const assignmentCompanyId = data?.companyId;
          if (assignmentCompanyId && assignmentCompanyId !== "mixed") {
            setCompanyId(assignmentCompanyId);
            setCompanyName(data?.companyName || "");
            return;
          }
          const runMeta = data?.runMeta || {};
          const map = new Map();
          Object.values(runMeta).forEach((m) => {
            const cid = m?.companyId;
            if (!cid || cid === "mixed") return;
            const cname = m?.companyName || cid;
            if (!map.has(cid)) map.set(cid, cname);
          });
          const opts = Array.from(map.entries()).map(([cid, cname]) => ({ companyId: cid, companyName: cname }));
          setCompanyOptions(opts);
          if (opts.length === 1) {
            setCompanyId(opts[0].companyId);
            setCompanyName(opts[0].companyName);
          }
          return;
        }
        const qy = query(
          collection(db, "assignments"),
          where("driverId", "==", driverId),
          where("date", "==", date),
          limit(20),
        );
        const scopedSnap = await getDocs(qy);
        if (cancelled || scopedSnap.empty) return;
        const map = new Map();
        scopedSnap.docs.forEach((d) => {
          const a = d.data() || {};
          const cid = a?.companyId;
          if (!cid || cid === "mixed") return;
          const cname = a?.companyName || cid;
          if (!map.has(cid)) map.set(cid, cname);
        });
        const opts = Array.from(map.entries()).map(([cid, cname]) => ({ companyId: cid, companyName: cname }));
        setCompanyOptions(opts);
        if (opts.length === 1) {
          setCompanyId(opts[0].companyId);
          setCompanyName(opts[0].companyName);
        }
      } catch (e) {
        console.error("Failed to resolve company for prestart:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, driverId, date]);

  useEffect(() => {
    if (!companyId) {
      setTrucks([]);
      return;
    }
    const qy = query(collection(db, "trucks"), where("companyId", "==", companyId));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.active !== false)
          .sort((a, b) => String(a.rego || "").localeCompare(String(b.rego || "")));
        setTrucks(list);
      },
      (err) => {
        console.error("Failed to load trucks for prestart:", err);
      },
    );
    return () => unsub();
  }, [companyId]);

  useEffect(() => {
    if (!hasAssignedVehicle) return;
    const matchedTruck =
      trucks.find((t) => assignedVehicleId && t.id === assignedVehicleId) ||
      trucks.find((t) => assignedVehicleRego && String(t.rego || "") === String(assignedVehicleRego)) ||
      null;
    setSelectedTruckId(matchedTruck?.id || assignedVehicleId || null);
    setRegistrationNo(matchedTruck?.rego || assignedVehicleRego || "");
    setUnitType(matchedTruck?.loadType || assignedVehicleLoadType || "");
  }, [trucks, hasAssignedVehicle, assignedVehicleId, assignedVehicleRego, assignedVehicleLoadType]);

  const handleRegoChange = (rego) => {
    setRegistrationNo(rego);
    const t = trucks.find((x) => x.rego === rego) || null;
    setSelectedTruckId(t ? t.id : null);
    if (t?.loadType) setUnitType(String(t.loadType));
  };

  const handleUnitTypeChange = (value) => {
    setUnitType(value);
    if (registrationNo) {
      const currentTruck = trucks.find((t) => t.rego === registrationNo);
      if (!currentTruck || String(currentTruck.loadType) !== String(value)) {
        setRegistrationNo("");
        setSelectedTruckId(null);
      }
    }
  };

  const save = async () => {
    if (!companyId) return alert("Please select a Company first.");
    if (!registrationNo.trim()) return alert("Please select Registration No.");
    if (!unitType.trim()) return alert("Please select Unit Type for this truck.");
    if (!email.trim()) return alert("Please enter Email Address.");
    if (!odoStart || Number.isNaN(Number(odoStart))) return alert("Please enter a numeric Odometer Start.");
    if (!hourMeterStart || Number.isNaN(Number(hourMeterStart))) return alert("Please enter a numeric Hour Meter Start.");
    if (!shiftStart) return alert("Please set Shift Start Time.");
    if (!decFit || !decLicense || !decInspect) return alert("Declaration items must be YES to operate.");

    const faultKeys = Object.entries(tri).filter(([, v]) => v === "fault").map(([k]) => k);
    for (const k of faultKeys) {
      if (!comments[k] || !comments[k].trim()) {
        return alert("Please add a comment for every item marked as 'Fault Identified'.");
      }
    }

    const completedAt = new Date().toLocaleString("en-AU", {
      timeZone: "Australia/Melbourne",
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
    });

    const payload = {
      driverId,
      date,
      completedAt,
      runKey: runKey || null,
      unitType,
      registrationNo,
      email,
      odometerStart: Number(odoStart),
      hourMeterStart: Number(hourMeterStart),
      shiftStartTime: shiftStart,
      declaration: {
        fitForDuty: decFit,
        licenseTrainingPPE: decLicense,
        inspectionDone: decInspect,
      },
      checklist: tri,
      comments,
      signature: sig || null,
      companyId: companyId || null,
      companyName: companyName || null,
      truckId: selectedTruckId || assignedVehicleId || null,
      vehicleId: selectedTruckId || assignedVehicleId || null,
      vehicleRego: registrationNo || assignedVehicleRego || null,
      vehicleLoadType: unitType || assignedVehicleLoadType || null,
      vehicleLabel:
        assignedVehicleLabel ||
        [registrationNo || assignedVehicleRego, unitType || assignedVehicleLoadType].filter(Boolean).join(" • ") ||
        null,
      updatedAt: serverTimestamp(),
    };

    const id = prestartDocId || `${driverId}_${date}`;
    await setDoc(doc(db, "prestarts", id), payload, { merge: true });
    onCompleted?.(payload);
    onClose?.();
  };

  const renderRow = ([key, label]) => {
    const status = tri[key];
    return (
      <div key={key} className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <span className="text-sm sm:w-1/2">{label}</span>
        <div className="sm:w-1/2">
          <TriSelect name={`tri_${key}`} value={status} onChange={(v) => setTriVal(key, v)} />
          {status === "fault" && (
            <input
              className="mt-2 w-full border rounded px-2 py-1 text-sm"
              placeholder="Describe the fault so we can fix it"
              value={comments[key] || ""}
              onChange={(e) => setComment(key, e.target.value)}
            />
          )}
        </div>
      </div>
    );
  };

  const vehicleSummary = assignedVehicleLabel || [registrationNo || assignedVehicleRego, unitType || assignedVehicleLoadType].filter(Boolean).join(" • ") || "Not assigned";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl w-[95%] max-w-3xl p-5 space-y-5 overflow-y-auto max-h-[90vh]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold">Daily Pre-Start Check</h3>
            {(runKey || hasAssignedVehicle) && (
              <div className="mt-1 text-sm text-gray-600">
                {runKey ? <div>Run: <span className="font-medium text-gray-900">{runKey}</span></div> : null}
                <div>Vehicle: <span className="font-medium text-gray-900">{vehicleSummary}</span></div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="px-3 py-1 rounded border">Close</button>
        </div>

        {!initialCompanyId && companyOptions.length > 1 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <label className="flex flex-col">
              Company *
              <select
                className="border rounded px-2 py-1 mt-1 bg-white"
                value={companyId || ""}
                onChange={(e) => {
                  const cid = e.target.value || "";
                  setCompanyId(cid || null);
                  const opt = companyOptions.find((x) => x.companyId === cid);
                  setCompanyName(opt?.companyName || "");
                }}
                disabled={!!initialCompanyId}
              >
                <option value="">Select company</option>
                {companyOptions.map((o) => (
                  <option key={o.companyId} value={o.companyId}>{o.companyName || o.companyId}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <label className="flex flex-col">
            Unit Type *
            <select className="border rounded px-2 py-1" value={unitType} onChange={(e) => handleUnitTypeChange(e.target.value)} disabled={hasAssignedVehicle || !companyId || !trucks.length}>
              <option value="">{!companyId ? "Select company first" : trucks.length ? "Select Unit Type" : "No trucks for company"}</option>
              {unitTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>

          <label className="flex flex-col">
            Registration No. *
            <select className="border rounded px-2 py-1" value={registrationNo} onChange={(e) => handleRegoChange(e.target.value)} disabled={hasAssignedVehicle || !companyId || !filteredTrucks.length}>
              <option value="">{!companyId ? "Select company first" : filteredTrucks.length ? "Select Registration" : unitType ? "No trucks for this unit type" : "Select unit type first"}</option>
              {filteredTrucks.map((t) => <option key={t.id} value={t.rego}>{t.rego}</option>)}
            </select>
          </label>

          <label className="flex flex-col">
            Email Address *
            <input type="email" className="border rounded px-2 py-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>

        <Section title="Pre-Start Checklist — All Mandatory (Group 1)">{CHECK_1.map(renderRow)}</Section>
        <Section title="Pre-Start Checklist — All Mandatory (Group 2)">{CHECK_2.map(renderRow)}</Section>
        <Section title="Pre-Start Checklist — All Mandatory (Continue)">{CHECK_3.map(renderRow)}</Section>

        <Section title="Driver's Pre-Start Declaration (must be YES to operate)">
          <YesNo label="I am fit to undertake my Driver role and allocated tasks." value={decFit} onChange={setDecFit} />
          <YesNo label="I have the correct, current and valid Driver's license, current training competency and appropriate PPE for operation of this vehicle." value={decLicense} onChange={setDecLicense} />
          <YesNo label="I have conducted the pre-start inspection as per the above checklist and am satisfied the vehicle is safe to operate to the limits of the inspection." value={decInspect} onChange={setDecInspect} />
        </Section>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <label className="flex flex-col">
            Odometer Start *
            <input type="number" className="border rounded px-2 py-1" value={odoStart} onChange={(e) => setOdoStart(e.target.value)} />
          </label>
          <label className="flex flex-col">
            Hour Meter Start *
            <input type="number" className="border rounded px-2 py-1" value={hourMeterStart} onChange={(e) => setHourMeterStart(e.target.value)} />
          </label>
          <label className="flex flex-col">
            Shift Start Time *
            <input type="time" className="border rounded px-2 py-1" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} />
          </label>
        </div>

        <div className="text-sm">
          <p className="mb-1 font-medium">Driver's Signature *</p>
          <SignaturePad onChange={setSig} />
        </div>

        {anyFault && (
          <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">
            ⚠️ One or more items marked as <strong>Fault Identified</strong>. Please notify your supervisor and do not operate until cleared.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 rounded border">Cancel</button>
          <button onClick={save} className="px-3 py-1 rounded bg-blue-600 text-white">Save Pre-Start</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h4 className="font-semibold">{title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function YesNo({ label, value, onChange }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <span className="text-sm">{label}</span>
      <div className="flex gap-3 text-xs">
        <label className="inline-flex items-center gap-1">
          <input
            type="radio"
            checked={value === true}
            onChange={() => onChange(true)}
          />{" "}
          Yes
        </label>
        <label className="inline-flex items-center gap-1">
          <input
            type="radio"
            checked={value === false}
            onChange={() => onChange(false)}
          />{" "}
          No
        </label>
      </div>
    </div>
  );
}
